<template>
    <div ref="rootElement" class="stretch-layout">
        <div class="flex items-center justify-between mb-8">
            <template v-for="(step,stepIndex) in steps" :key="stepIndex">
                <wizard-step-header :done="submitValue > stepIndex + 1"
									:model-value="submitValue === stepIndex + 1" :step="stepIndex + 1" :title="step.title"
                                    :color="color"
                                    :hide-details="!visibleSteps.includes(stepIndex)"
                                    :clickable="furthestIndex >= stepIndex + 1"
                                    :style="steps.length > maxVisibleSteps ? `max-width: ${100/(maxVisibleSteps+1)}%` : ''"
                                    @click="jump(stepIndex + 1)"
                                    :subtitle="step.subtitle"/>

                    <divider v-if="stepIndex < steps.length - 1" :key="'divider-' + stepIndex" margin="8"/>
            </template>
        </div>

        <ajax-form :action="activeStep && activeStep.action ? activeStep.action : action"
                   :method="activeStep && activeStep.method ? activeStep.method : method"
                   :data="activeStep && activeStep.data ? activeStep.data : data"
                   :alert="alert"
				   :persist-success-loading="persistSuccessLoading && isLast"
                   :notification="notification"
                   :lang-params="langParams"
                   v-model:loading="isLoading"
                   v-model:valid="isValid" ref="form" @success="handleSuccess" @error="handleError" class="stretch-layout relative">
			<template v-for="(step,stepIndex) in steps" :key="stepIndex">
				<transition name="wizard-slide-transition" mode="out-in">
					<div v-if="(stepIndex + 1) === submitValue" class="wizard-slide stretch-layout h-full text-left w-full sm:px-12 md:px-16 pt-8" :key="stepIndex">
						<alert v-if="step.description" type="info" text :content="step.description" hide-icon-on-mobile class="mb-24 sm:mb-32"/>

						<div class="flex-grow-1">
							<slot :name="step.name"/>
						</div>

						<wrapper v-if="!disabled" stretch class="mt-32 wizard-slide-buttons">
							<btn v-if="!isFirst" @click.prevent="back()" large>
								<Icon :icon="icons.back"/>
								<span :class="{'none sm:block': $slots.buttons}" class="text-xs sm:text-sm">{{ $t('action.back') }}</span>
							</btn>

							<slot name="buttons"/>

							<btn color="success" @click.prevent="validateAndNext()" :loading="isLoading" :disabled="isLoading" large>
								<Icon v-if="isLast" :icon="icons.check"/>
								<span class="text-xs sm:text-sm">{{ activeStep && activeStep.next ? activeStep.next : $t('action.next') }}</span>
								<Icon v-if="!isLast" :icon="icons.next"/>
							</btn>
						</wrapper>
					</div>
				</transition>
			</template>
        </ajax-form>
    </div>
</template>

<script>
import Inputtable from "../../mixins/Inputtable";
    import Loadable from "../../mixins/Loadable";
    import {scrollToSelector} from "@spoferan/spoferan-ts-core";
    import {isFunction} from "@spoferan/spoferan-ts-core";
	import {backIcon, checkIcon, nextIcon} from "../../config";

    export default {

        mixins: [Inputtable, Loadable],

        name: 'FormWizard',

		emits: ['update:valid', 'finished', 'success', 'error'],

        props: {

            color: {
                type: String,
                default: 'primary'
            },

            // The value of the input
			modelValue: {
                default: 1
            },

            // The data to send to the server after all steps of the wizard have been completed
            // Can be specified for each individual step to submit every step individually
            data: {
                type: Object,
                default: null
            },

            // The URL where to send the data of every step
            // Can be specified for each individual step to send the data of the step to another URL
            action: {
                type: String,
                required: true
            },

            // The method to use when sending the data of every step
            // Can be specified for each individual step to send the data of the step with another method
            method: {
                type: String,
                default: 'post'
            },

            // The valid state to keep in sync with the form's isValid prop
            valid: {
                type: Boolean,
                default: false
            },

            // The individual wizard steps
            steps: {
                type: Array,
                required: true
            },

            // The lang key of the 'alert' lang file to show an alert message
            alert: {
                type: String,
                default: null
            },

            // The lang key of the 'notification' lang file to show a notification message
            // Can also be 'true' to show a standard notification message
            notification: {
                type: [String, Boolean],
                default: null
            },

            // An object to replace params of the notification or alert message
            langParams: {
                type: Object,
                default: null
            },

			disabled: {
				type: Boolean,
				default: false
			},

			// Whether the loading state should be persisted after a successful submit on the final step.
			// Useful when heavy actions are executed after a successful submit, e.g., a redirect, which might take some time
			// to prevent the user from submitting the form again.
			persistSuccessLoading: {
				type: Boolean,
				default: false
			},
        },

        data() {
            return {
                isValid: false,

                // The furthest index reached by the user
                furthestIndex: 1,

				icons: {
					check: checkIcon,
					next: nextIcon,
					back: backIcon,
				}
            }
        },

        computed: {
            isLast() {
                return this.steps.length === this.submitValue;
            },

            isFirst() {
                return this.submitValue === 1;
            },

            activeStep() {
                return this.steps[this.submitValue - 1]
            },

            maxVisibleSteps() {
                if (this.$breakpoint.xsOnly) {
                    return 2;
                }
                if (this.$breakpoint.mdAndDown) {
                    return 5;
                }

                return 8;
            },

            // The index of the steps of which to display the full details
            visibleSteps() {
                if (this.steps.length <= this.maxVisibleSteps) {
                    return [...Array(this.steps.length).keys()];
                }

                const visible = [this.modelValue - 1, this.steps.length - 1];
                let maxNextStepsCount = this.steps.length - this.modelValue - 1;
                let maxPrevStepsCount = this.modelValue - 1;
                
                let nextStepsCount = Math.ceil((this.maxVisibleSteps - visible.length) / 2);
                let overflowNextStepsCount = nextStepsCount - maxNextStepsCount
                overflowNextStepsCount = overflowNextStepsCount > 0 ? overflowNextStepsCount : 0

                let prevStepsCount = Math.floor((this.maxVisibleSteps - visible.length) / 2);
                let overflowPrevStepsCount = prevStepsCount - maxPrevStepsCount
                overflowPrevStepsCount = overflowPrevStepsCount > 0 ? overflowPrevStepsCount : 0

                nextStepsCount = Math.min(nextStepsCount + overflowPrevStepsCount, maxNextStepsCount)
                for (let i = this.modelValue; i < this.modelValue + nextStepsCount; i++) {
                    visible.push(i);
                }

                prevStepsCount = Math.min(prevStepsCount + overflowNextStepsCount, maxPrevStepsCount)
                for (let i = this.modelValue - 1; i >= this.modelValue - 1 - prevStepsCount; i--) {
                    visible.push(i);
                }

                return visible;
            }
        },

        watch: {
            isValid(isValid) {
                this.$emit('update:valid', isValid)
            },

            submitValue(index, oldIndex) {
                this.furthestIndex = index > this.furthestIndex ? index : this.furthestIndex
                if (oldIndex) {
                    this.focus();
                }
            },
        },

        methods: {

            /**
             * Navigates to the next wizard screen by validating the current one.
             * If the validation fails, we highlight the errors.
             */
            next() {
                // Check if the individual data of the step shall be submitted
                if (this.activeStep.data) {
					try {
						this.$refs.form.validationSubmit();
					} catch (e) {
						console.error(e)
					}
                } else if (this.validate()) {
                    this.submitValue++;
                }
            },

			// TODO: Expose when migrating to Nuxt 3 composition API as called via event creation form
			validate() {
				return !this.$refs.form || this.$refs.form.validate(true)
			},

            jump(stepIndex) {
                // We only allow a jump forward if the current step is valid and the jump step would be the next step or if the user has already reached the jump step before
                if (!this.disabled && (this.isValid && stepIndex <= this.submitValue + 1) || this.furthestIndex >= stepIndex) {
                    this.submitValue = stepIndex
                }
            },

            /**
             * Finishes the wizard by validating the data.
             * If the validation fails, we highlight the errors.
             */
            finish() {
                if (this.data || this.activeStep.data) {
					try {
						this.$refs.form.validationSubmit();
					} catch (e) {
						console.error(e)
					}
                } else if (this.isValid) {
					if (this.persistSuccessLoading) {
						this.isLoading = true;
					}
                    this.$emit('finished')
                }
            },

            handleSuccess(responseData, fullResponse) {

                // Check if a success handler for the step exists which overwrites the global success handler
                if (isFunction(this.activeStep.success)) {
                    this.activeStep.success(responseData, fullResponse);
                } else {
                    this.$emit('success', responseData, fullResponse)
                }

                // If the current step is not the last, we proceed to the next step
                if (!this.isLast) {

					// When using the persistSuccessLoading option, the loader might still be active when the last step was inserted dynamically,
					// whereby we safely reset the loading state here
					this.isLoading = false;

                    // We only proceed to the next step if not forbidden by the active step
                    if (!this.activeStep.stay) {
                        this.submitValue++;
                    }
                } else {
                    this.$emit('finished')
                }
            },

            handleError(status, response) {
                this.$emit('error', status, response)
            },

            /**
             * Activates the next step with validation.
			 *
			 * TODO: Expose when migrating to Nuxt 3 composition API as called via event creation form
             */
            validateAndNext() {
                if (isFunction(this.activeStep.beforeNext)) {
                    this.activeStep.beforeNext().then((result) => {
                        if (result) {
                            if (this.isLast) {
                                this.finish();
                            } else {
                                this.next()
                            }
                        }
                    })
                } else {
                    if (this.isLast) {
                        this.finish();
                    } else {
                        this.next()
                    }
                }
            },

            /**
             * Activates the previous step.
             */
            back() {
                if (!this.isFirst) {
                    this.submitValue--;
                }
            },

            focus() {
				scrollToSelector(this.$refs.rootElement)
            }
        }
    }
</script>

<style>
/* ==========================================================================
   Mixins
   ========================================================================== */
.wizard-slide.wizard-slide-transition-enter-active, .wizard-slide.wizard-slide-transition-leave-active {
  -ms-transition: 0.4s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
  -o-transition: 0.4s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
  -moz-transition: 0.4s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
  -webkit-transition: 0.4s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
  transition: 0.4s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
}
.wizard-slide.wizard-slide-transition-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
.wizard-slide.wizard-slide-transition-leave-active .wizard-slide-buttons {
  display: none;
}
.wizard-slide:first-child.wizard-slide-transition-enter-from, .wizard-slide:first-child.wizard-slide-transition-leave-to {
  transform: translateX(-110%);
}
.wizard-slide:last-child.wizard-slide-transition-enter-from, .wizard-slide:last-child.wizard-slide-transition-leave-to {
  transform: translateX(110%);
}
</style>